<script setup>
import { router } from "@inertiajs/vue3";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";

const {
  type = "primary",
  size = "medium",
  submit = false,
  href,
  target,
  download = null,
  callback,
  icon,
  iconSize = null,
  leftIcon,
  loading = null,
  ping = false,
} = defineProps({
  type: {
    type: String,
    required: false,
  },
  size: {
    type: String,
    required: false,
  },
  submit: {
    type: Boolean,
    required: false,
  },
  href: {
    type: String,
    required: false,
  },
  target: {
    type: String,
    required: false,
  },
  download: {
    type: Boolean,
    required: false,
  },
  callback: {
    type: Function,
    required: false,
  },
  icon: {
    required: false,
    default: null,
  },
  iconSize: {
    type: [String, null],
    required: false,
  },
  leftIcon: {
    required: false,
    default: null,
  },
  loading: {
    type: Boolean,
    required: false,
  },
  ping: {
    type: Boolean,
    required: false,
  },
});

function click(event, blank = false) {
  if (!submit) {
    if (href) {
      if (target === "_blank" || event.ctrlKey || blank) {
        window.open(href, "_blank");
      } else if (download) {
        window.location.href = href;
      } else {
        router.visit(href);
      }
    } else if (callback) {
      callback();
    } else {
      console.log("No action defined.");
    }
  }
}
</script>

<template>
  <button
    :type="submit ? 'submit' : 'button'"
    class="relative flex items-center justify-center gap-2 overflow-visible rounded duration-150 disabled:cursor-not-allowed disabled:opacity-60"
    :class="{
      'bg-indigo-600 text-white enabled:hover:bg-indigo-950':
        type === 'primary',
      'bg-gray-200 text-indigo-600 enabled:hover:bg-indigo-600 enabled:hover:text-white':
        type === 'secondary',
      'bg-red-500 text-white enabled:hover:bg-red-400': type === 'danger',
      'bg-green-500 text-white enabled:hover:bg-green-400': type === 'success',
      'text-indigo-600 enabled:hover:underline': type === 'transparent',
      'px-3 py-1 text-sm': size === 'small',
      'px-4 py-2 text-sm': size === 'medium',
      'px-4 py-2 text-base': size === 'large',
    }"
    @click="click"
    @mousedown.middle="(event) => click(event, true)"
    :disabled="loading"
  >
    <template v-if="icon">
      <component
        v-if="icon && !loading"
        :is="icon"
        :class="[iconSize ?? 'size-5', 'flex-shrink-0 flex-grow-0']"
      />

      <SpinnerIcon
        v-else-if="loading"
        class="size-5 animate-spin"
        :class="{
          'fill-indigo-600 text-white': type === 'primary',
        }"
      />
    </template>

    <template v-else>
      <component v-if="leftIcon && !loading" :is="leftIcon" class="size-4" />

      <SpinnerIcon
        v-else-if="loading"
        class="size-4 animate-spin"
        :class="{
          'fill-indigo-600 text-white': type === 'primary',
        }"
      />

      <slot />
    </template>

    <span v-if="ping" class="absolute -right-1 -top-1 flex size-3">
      <span
        class="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75"
      >
      </span>
      <span
        class="relative inline-flex size-3 rounded-full bg-yellow-500"
      ></span>
    </span>
  </button>
</template>
